import React from 'react';
import { motion } from 'framer-motion';

const Hero: React.FC = () => {
  const stats = [
    { value: '9', label: 'Capítulos técnicos' },
    { value: '160+', label: 'Miembros activos' },
    { value: '12+', label: 'Eventos anuales' },
  ];

  // Container animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 24 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8 },
    },
  };

  return (
    <section
      id="home"
      className="relative overflow-hidden bg-white pt-24 pb-12 sm:pt-32 sm:pb-16 lg:pt-40 lg:pb-24"
    >
      {/* Ambient background layers - subtler for light mode */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-32 -left-32 w-[600px] h-[600px] rounded-full blur-[140px] opacity-[0.08] bg-gradient-to-r from-cyan-400/30 to-transparent" />
        <div className="absolute top-1/3 -right-40 w-[500px] h-[500px] rounded-full blur-[130px] opacity-[0.06] bg-gradient-to-l from-indigo-400/20 to-transparent" />
        <div className="absolute bottom-0 left-1/3 w-[400px] h-[300px] rounded-full blur-[120px] opacity-[0.05] bg-gradient-to-t from-blue-400/15 to-transparent" />
      </div>

      {/* Subtle light grid texture */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(rgba(15,23,42,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(15,23,42,0.1) 1px, transparent 1px)',
          backgroundSize: '100px 100px',
        }}
      />

      <div className="relative z-10 mx-auto max-w-7xl px-6 sm:px-8">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center"
        >
          {/* LEFT SIDE: Content block */}
          <motion.div className="flex flex-col gap-8 lg:gap-10">
            {/* Status badge */}
            <motion.div variants={itemVariants}>
              <div className="inline-flex items-center gap-2.5 px-3.5 py-2 rounded-lg bg-slate-50 border border-slate-200 backdrop-blur-sm w-fit shadow-sm">
                <div className="w-1.5 h-1.5 rounded-full bg-cyan-500 shadow-[0_0_8px_rgba(6,182,212,0.5)]" />
                <span className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">
                  IEEE ESPOCH
                </span>
              </div>
            </motion.div>

            {/* Main heading */}
            <motion.div variants={itemVariants} className="space-y-6">
              <h1 className="text-5xl sm:text-6xl lg:text-[3.5rem] xl:text-7xl font-extrabold leading-[1.15] tracking-tight text-slate-900">
                Conecta tu{' '}
                <span className="relative inline-block">
                  <span className="absolute inset-x-0 bottom-2 h-3 bg-cyan-100/60 -z-10" />
                  <span className="relative text-cyan-600">
                    talento
                  </span>
                </span>
                {'\n'}con la{' '}
                <span className="relative inline-block">
                  <span className="absolute inset-x-0 bottom-2 h-3 bg-indigo-100/60 -z-10" />
                  <span className="relative text-indigo-600">
                    innovación
                  </span>
                </span>
              </h1>
            </motion.div>

            {/* Subheading */}
            <motion.p
              variants={itemVariants}
              className="text-lg sm:text-xl text-slate-600 leading-relaxed font-normal max-w-lg"
            >
              Una comunidad global de ingeniería donde el talento técnico y el liderazgo convergen para crear impacto real.
            </motion.p>

            {/* CTA buttons */}
            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-4 pt-4">
              <motion.a
                href="#chapters"
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
                className="inline-flex items-center justify-center px-8 py-3.5 rounded-xl font-bold text-sm uppercase tracking-[0.2em] transition-all duration-300 bg-cyan-600 text-white shadow-premium hover:bg-cyan-500"
              >
                Explorar Capítulos
              </motion.a>
              <motion.a
                href="#team"
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
                className="inline-flex items-center justify-center px-8 py-3.5 rounded-xl font-bold text-sm uppercase tracking-[0.2em] text-slate-700 transition-all duration-300 bg-white border border-slate-200 hover:bg-slate-50 hover:border-slate-300 shadow-sm"
              >
                Conocer al Equipo
              </motion.a>
            </motion.div>

            {/* Stats */}
            <motion.div
              variants={itemVariants}
              className="grid grid-cols-3 gap-6 sm:gap-8 pt-8 border-t border-slate-100"
            >
              {stats.map((stat, idx) => (
                <div key={idx} className="flex flex-col gap-2">
                  <div className="text-3xl sm:text-4xl font-black text-slate-900 leading-tight">
                    {stat.value}
                  </div>
                  <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">
                    {stat.label}
                  </div>
                </div>
              ))}
            </motion.div>
          </motion.div>

          {/* RIGHT SIDE: Visual element */}
          <motion.div
            variants={itemVariants}
            className="relative hidden lg:flex items-center justify-center"
          >
            <div className="relative w-full aspect-square max-w-md">
              {/* Outer glow layers - softer for light mode */}
              <motion.div
                animate={{
                  scale: [1, 1.05, 1],
                  opacity: [0.3, 0.5, 0.3],
                }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                className="absolute inset-0 rounded-[2.5rem] bg-gradient-to-br from-cyan-400/20 to-transparent blur-3xl"
              />

              {/* Card visual */}
              <div className="absolute inset-0 rounded-[2.5rem] overflow-hidden bg-white shadow-premium border border-slate-100">
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-50/50 via-transparent to-transparent opacity-60" />
                
                {/* Subtle grid pattern */}
                <div
                  className="absolute inset-0 opacity-[0.05]"
                  style={{
                    backgroundImage:
                      'linear-gradient(45deg, #0f172a 1px, transparent 1px), linear-gradient(-45deg, #0f172a 1px, transparent 1px)',
                    backgroundSize: '40px 40px',
                  }}
                />

                {/* Animated content */}
                <div className="absolute inset-0 flex flex-col items-center justify-center p-8 gap-8">
                  <motion.img
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1, delay: 0.5 }}
                    src="/assets/logos_rama_general_completos/Logo_completo_azul.png"
                    alt="IEEE Logo"
                    className="h-16 w-auto object-contain drop-shadow-sm"
                  />
                  
                  <div className="text-center space-y-2">
                    <motion.div
                      animate={{ y: [0, -4, 0] }}
                      transition={{ duration: 3, repeat: Infinity }}
                      className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-indigo-600"
                    >
                      160+
                    </motion.div>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-bold">
                      Miembros Activos
                    </p>
                  </div>
                </div>
              </div>

              {/* Decorative accent */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
                className="absolute -inset-4 rounded-[3rem] border border-cyan-500/10 pointer-events-none"
                style={{
                  borderStyle: 'dashed',
                  borderWidth: '1px',
                }}
              />
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
